import React, { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import videojs from "video.js";

import TabNavigation from "./TabNavigation";
import DigitalSubTabs from "./DigitalSubTabs";
import VideoSection from "./VideoSection";
import ImageSlider from "./ImageSlider";
import VideoJS from "./VideoJS"; // Video.js 래퍼 컴포넌트
import { getPortfolioData, digitalCampaignSubTabs } from "../data/portfolioData";
import { getStaticFileUrl } from "../utils/config";

// === 유틸 함수 / 더미데이터 (변경 없음) ===
const isYouTubeLink = (url) =>
  url.includes("youtube.com") || url.includes("youtu.be");

const isVideoFile = (url) =>
  url.endsWith(".mp4") || url.endsWith(".webm") || url.endsWith(".ogg");

const chunkArrayMin6 = (array, size) => {
  const maxCount = Math.max(array.length, size);
  const totalSlides = Math.ceil(maxCount / size);
  const result = [];

  for (let i = 0; i < totalSlides; i++) {
    const slice = array.slice(i * size, i * size + size);
    while (slice.length < size) {
      slice.push(null);
    }
    result.push(slice);
  }
  return result;
};

// 포트폴리오 데이터는 이제 별도 파일에서 관리됨

// 디지털콘텐츠 서브탭 내 영상 필터 함수 (변경 없음)
function getDigitalVideoItems(allContents, subTabLabel) {
  const filtered = allContents.filter((item) => item.subTab === subTabLabel);
  const videoSourceItems = filtered.filter(
    (item) => isYouTubeLink(item.src) || isVideoFile(item.src)
  );
  return videoSourceItems.map((item, idx) => ({
    id: idx + 1000,
    title: `${item.title}`,
    description: `${item.description}`,
    videoSrc: isYouTubeLink(item.src) ? item.src : getStaticFileUrl(item.src),
  }));
}

export function TabbedContent() {
  // 포트폴리오 데이터 상태
  const [tabData, setTabData] = useState([]);
  
  // 메인 탭 & 서브탭 상태
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const [subTabIndex, setSubTabIndex] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;
  const [slideIndex, setSlideIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const playerRef = useRef(null);

  // 포트폴리오 데이터 로드
  useEffect(() => {
    const loadPortfolioData = async () => {
      try {
        const portfolioData = await getPortfolioData();
        console.log('포트폴리오 데이터 로드됨:', portfolioData);
        setTabData(portfolioData);
      } catch (error) {
        console.error('포트폴리오 데이터 로드 실패:', error);
      }
    };
    loadPortfolioData();
  }, []);

  // 윈도우 사이즈 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 현재 탭 데이터
  const currentTab = tabData[currentTabIndex];
  console.log('현재 탭 데이터:', currentTab, 'tabData 길이:', tabData.length);

  // 탭 전환 시 해당 탭의 첫 번째 영상을 선택하도록 설정
  useEffect(() => {
    if (!currentTab) return;
    
    if (
      currentTabIndex === 0 ||
      currentTabIndex === 1 ||
      currentTabIndex === 3
    ) {
      const firstVideo = currentTab.videos?.[0] || null;
      setSelectedVideo(firstVideo);
      setCurrentPage(1);
    } else if (currentTabIndex === 2) {
      // 디지털 콘텐츠 탭에서는 서브탭 인덱스를 0으로 설정하고 "유튜브 영상" 서브탭의 첫 번째 영상 선택
      setSubTabIndex(0);
      const label = digitalCampaignSubTabs[0].label;
      const nextVideoItems = getDigitalVideoItems(currentTab.contents || [], label);
      setSelectedVideo(nextVideoItems.length > 0 ? nextVideoItems[0] : null);
      setCurrentPage(1);
    } else {
      setSelectedVideo(null);
    }
    setSlideIndex(0);
  }, [currentTabIndex, currentTab]);

  // 선택된 영상이 변경되면 플레이어가 새로 생성되도록 key 전달과 동시에 자동재생 시도
  useEffect(() => {
    if (selectedVideo && playerRef.current) {
      // 약간의 지연 후 자동 재생 시도 (브라우저 정책에 따라 음소거 필요)
      const timer = setTimeout(() => {
        playerRef.current
          .play()
          .catch((error) => videojs.log("Auto play error:", error));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedVideo]);

  // ========================================
  // A) "홍보영상" & "오프라인 행사" 탭 데이터 계산
  // ========================================
  let videoItems = [];
  let totalVideoPages = 0;
  let currentVideoItems = [];
  if (currentTab && (currentTabIndex === 0 || currentTabIndex === 1 || currentTabIndex === 3)) {
    videoItems = currentTab.videos || [];
    totalVideoPages = Math.ceil(videoItems.length / pageSize);
    const videoStart = (currentPage - 1) * pageSize;
    const videoEnd = videoStart + pageSize;
    currentVideoItems = videoItems.slice(videoStart, videoEnd);
  }

  // ========================================
  // B) "디지털 콘텐츠" 탭 - 서브탭별 영상/이미지 계산
  // ========================================
  let digitalAllContents = [];
  if (currentTab && currentTabIndex === 2) {
    digitalAllContents = currentTab.contents || [];
  }
  const subTabLabel = digitalCampaignSubTabs[subTabIndex]?.label || '';
  const digitalVideoItems = getDigitalVideoItems(digitalAllContents, subTabLabel);
  const totalDigitalVideoPages = Math.ceil(digitalVideoItems.length / pageSize);
  const digitalStart = (currentPage - 1) * pageSize;
  const digitalEnd = digitalStart + pageSize;
  const currentDigitalVideoItems = digitalVideoItems.slice(
    digitalStart,
    digitalEnd
  );
  const subTabHasVideo = digitalVideoItems.length > 0;
  // 서브탭에 맞는 이미지 항목 필터링 (현재 서브탭과 일치 && 영상이 아닌 항목)
  const digitalImageItems = digitalAllContents.filter(
    (item) =>
      item.subTab === subTabLabel &&
      !isYouTubeLink(item.src) &&
      !isVideoFile(item.src)
  );

  // ========================================
  // C) 다른 탭 (방송프로그램)의 이미지 처리
  // ========================================
  let otherTabContents = [];
  if (
    currentTab &&
    currentTabIndex !== 0 &&
    currentTabIndex !== 1 &&
    currentTabIndex !== 2 &&
    currentTabIndex !== 3
  ) {
    otherTabContents = currentTab.contents || [];
  }

  // ========================================
  // D) 이미지 슬라이드 처리
  // ========================================
  let imageItems = [];
  if (currentTabIndex === 2) {
    if (!subTabHasVideo) {
      imageItems = digitalImageItems;
    }
  } else if (currentTabIndex !== 1 && currentTabIndex !== 3) {
    imageItems = otherTabContents;
  }
  const itemsPerSlide = isMobile ? 6 : 6;
  const imageSlides = chunkArrayMin6(imageItems, itemsPerSlide);

  // ========================================
  // 이벤트 핸들러
  // ========================================
  const handleTabChange = (idx) => {
    setCurrentTabIndex(idx);
  };

  const handleSubTabChange = (newSubTabIndex) => {
    setSubTabIndex(newSubTabIndex);
    setCurrentPage(1);
    setSlideIndex(0);
    if (currentTab) {
      const label = digitalCampaignSubTabs[newSubTabIndex]?.label || '';
      const nextVideoItems = getDigitalVideoItems(currentTab.contents || [], label);
      setSelectedVideo(nextVideoItems.length > 0 ? nextVideoItems[0] : null);
    }
  };

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    let maxPage =
      currentTabIndex === 0 || currentTabIndex === 1 || currentTabIndex === 3
        ? totalVideoPages
        : totalDigitalVideoPages;
    if (currentPage < maxPage) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevSlide = () => {
    setSlideIndex((prev) => Math.max(prev - 1, 0));
  };
  const handleNextSlide = () => {
    setSlideIndex((prev) => Math.min(prev + 1, imageSlides.length - 1));
  };

  const isLeftDisabled = slideIndex === 0;
  const isRightDisabled =
    imageSlides.length === 0 || slideIndex === imageSlides.length - 1;

  // Video.js 옵션 설정
  // 유튜브 링크일 경우 브라우저 autoplay 정책을 위해 muted:true로 설정
  const videoJsOptions = selectedVideo
    ? {
        autoplay: true,
        controls: true,
        responsive: true,
        fluid: true,
        // 유튜브 링크는 반드시 음소거해야 자동재생 가능
        muted: isYouTubeLink(selectedVideo.videoSrc) ? true : false,
        techOrder: isYouTubeLink(selectedVideo.videoSrc)
          ? ["youtube"]
          : ["html5"],
        sources: [
          {
            src: isYouTubeLink(selectedVideo.videoSrc) 
              ? selectedVideo.videoSrc 
              : getStaticFileUrl(selectedVideo.videoSrc),
            type: isYouTubeLink(selectedVideo.videoSrc)
              ? "video/youtube"
              : "video/mp4",
          },
        ],
      }
    : {
        autoplay: false,
        controls: true,
        responsive: true,
        fluid: true,
        sources: [],
      };

  const handlePlayerReady = (player) => {
    playerRef.current = player;
    player.on("waiting", () => {
      videojs.log("player is waiting");
    });
    player.on("dispose", () => {
      videojs.log("player will dispose");
    });
    // 플레이어가 준비된 후, 약간의 지연 후 재생 시도
    setTimeout(() => {
      player.play().catch((error) => {
        videojs.log("autoplay failed:", error);
      });
    }, 250);
  };

  // ========================================
  // 최종 렌더링
  // ========================================
  
  // 데이터가 로드되지 않았을 때
  if (tabData.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: "70vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "2rem",
        }}
      >
        <Typography variant="h6">포트폴리오 데이터를 불러오는 중...</Typography>
      </Box>
    );
  }
  
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "70vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: "2rem",
      }}
    >
      {/* 상단 탭 */}
      <TabNavigation
        tabData={tabData}
        currentTabIndex={currentTabIndex}
        onChangeTab={handleTabChange}
      />

      {/* 디지털 콘텐츠 탭: 서브탭 */}
      {currentTabIndex === 2 && (
        <DigitalSubTabs
          subTabs={digitalCampaignSubTabs}
          currentSubTabIndex={subTabIndex}
          onChangeSubTab={handleSubTabChange}
        />
      )}

      {/* 1) 방송프로그램, 홍보영상, 오프라인 행사 탭 (비디오 섹션) */}
      {(currentTabIndex === 0 ||
        currentTabIndex === 1 ||
        currentTabIndex === 3) &&
        selectedVideo && (
          <VideoSection
            key={selectedVideo.id} // selectedVideo 변경 시 재마운트
            selectedVideo={selectedVideo}
            videoItems={currentVideoItems}
            totalPages={totalVideoPages}
            currentPage={currentPage}
            onSelectVideo={handleVideoSelect}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            videoJsOptions={videoJsOptions}
            onPlayerReady={handlePlayerReady}
          />
        )}

      {/* 2) 디지털 콘텐츠 탭 (영상이 있는 경우) */}
      {currentTabIndex === 2 && subTabHasVideo && selectedVideo && (
        <VideoSection
          key={selectedVideo.id} // 변경 시 재마운트
          selectedVideo={selectedVideo}
          videoItems={currentDigitalVideoItems}
          totalPages={totalDigitalVideoPages}
          currentPage={currentPage}
          onSelectVideo={handleVideoSelect}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
          videoJsOptions={videoJsOptions}
          onPlayerReady={handlePlayerReady}
        />
      )}

      {/* 3) 이미지 슬라이더 (영상 없는 경우) */}
      {currentTabIndex !== 0 &&
        currentTabIndex !== 1 &&
        currentTabIndex !== 3 &&
        !(currentTabIndex === 2 && subTabHasVideo) && (
          <ImageSlider
            imageSlides={imageSlides}
            slideIndex={slideIndex}
            onPrevSlide={handlePrevSlide}
            onNextSlide={handleNextSlide}
            isLeftDisabled={isLeftDisabled}
            isRightDisabled={isRightDisabled}
          />
        )}
    </Box>
  );
}

export default TabbedContent;
